import React, { useState, useEffect } from 'react';
import { History, CheckCircle, XCircle, LogIn, LogOut, UserPlus, UserMinus, Edit, Trash2, RefreshCw } from 'lucide-react';
import { useAdmin } from '../context/AdminContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_CONFIG = {
  login_success: { icon: LogIn, color: 'text-green-600 bg-green-50', label: { pt: 'Login', en: 'Login' } },
  login_failed: { icon: LogIn, color: 'text-red-600 bg-red-50', label: { pt: 'Login Falhado', en: 'Failed Login' } },
  logout: { icon: LogOut, color: 'text-gray-600 bg-gray-50', label: { pt: 'Logout', en: 'Logout' } },
  create_user: { icon: UserPlus, color: 'text-blue-600 bg-blue-50', label: { pt: 'Criar Utilizador', en: 'Create User' } },
  update_user: { icon: Edit, color: 'text-yellow-600 bg-yellow-50', label: { pt: 'Editar Utilizador', en: 'Edit User' } },
  delete_user: { icon: UserMinus, color: 'text-red-600 bg-red-50', label: { pt: 'Excluir Utilizador', en: 'Delete User' } },
  update_status: { icon: Edit, color: 'text-purple-600 bg-purple-50', label: { pt: 'Alterar Estado', en: 'Update Status' } },
};

const AdminAuditLog = ({ language }) => {
  const { token, isSuperAdmin } = useAdmin();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/audit-logs?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString(language === 'pt' ? 'pt-PT' : 'en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const filteredLogs = logs.filter(log => {
    if (filter === 'all') return true;
    if (filter === 'logins') return log.action.includes('login');
    if (filter === 'users') return log.action.includes('user');
    if (filter === 'failed') return !log.success;
    return true;
  });

  if (!isSuperAdmin()) {
    return (
      <div className="text-center py-12">
        <History size={48} className="mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">
          {language === 'pt' 
            ? 'Apenas Super Admins podem ver o histórico' 
            : 'Only Super Admins can view audit logs'}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-heading font-semibold flex items-center gap-2">
          <History size={24} />
          {language === 'pt' ? 'Histórico de Atividades' : 'Activity History'}
        </h2>
        <div className="flex items-center gap-3">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-10 px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">{language === 'pt' ? 'Todas as ações' : 'All actions'}</option>
            <option value="logins">{language === 'pt' ? 'Apenas logins' : 'Logins only'}</option>
            <option value="users">{language === 'pt' ? 'Gestão de utilizadores' : 'User management'}</option>
            <option value="failed">{language === 'pt' ? 'Ações falhadas' : 'Failed actions'}</option>
          </select>
          <button
            onClick={fetchLogs}
            className="h-10 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={16} />
            {language === 'pt' ? 'Atualizar' : 'Refresh'}
          </button>
        </div>
      </div>

      {loading ? (
        <p>{language === 'pt' ? 'Carregando...' : 'Loading...'}</p>
      ) : filteredLogs.length === 0 ? (
        <p className="text-gray-600">{language === 'pt' ? 'Nenhum registo encontrado' : 'No records found'}</p>
      ) : (
        <div className="space-y-2">
          {filteredLogs.map((log) => {
            const config = ACTION_CONFIG[log.action] || { 
              icon: History, 
              color: 'text-gray-600 bg-gray-50', 
              label: { pt: log.action, en: log.action } 
            };
            const ActionIcon = config.icon;
            
            return (
              <div 
                key={log.id} 
                className={`border rounded-lg p-4 bg-white shadow-sm flex items-center gap-4 ${!log.success ? 'border-red-200' : 'border-gray-200'}`}
                data-testid={`log-${log.id}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.color}`}>
                  <ActionIcon size={20} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">
                      {config.label[language]}
                    </span>
                    {log.success ? (
                      <CheckCircle size={14} className="text-green-500" />
                    ) : (
                      <XCircle size={14} className="text-red-500" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600 truncate">
                    <span className="font-medium">{log.user_name || log.user_email}</span>
                    {log.details && <span className="text-gray-400"> - {log.details}</span>}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-gray-400 mt-1">
                    <span>{formatDate(log.timestamp)}</span>
                    {log.ip_address && <span>IP: {log.ip_address}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">
          {language === 'pt' ? 'Resumo' : 'Summary'}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-gray-900">{logs.length}</p>
            <p className="text-xs text-gray-500">{language === 'pt' ? 'Total de ações' : 'Total actions'}</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-600">{logs.filter(l => l.action === 'login_success').length}</p>
            <p className="text-xs text-gray-500">{language === 'pt' ? 'Logins bem-sucedidos' : 'Successful logins'}</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-red-600">{logs.filter(l => l.action === 'login_failed').length}</p>
            <p className="text-xs text-gray-500">{language === 'pt' ? 'Logins falhados' : 'Failed logins'}</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-blue-600">{logs.filter(l => l.action.includes('user')).length}</p>
            <p className="text-xs text-gray-500">{language === 'pt' ? 'Ações de utilizadores' : 'User actions'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAuditLog;
